"use client"

import { vote } from "@/actions/vote"
import { ChevronUp } from "lucide-react"
import { Session } from "next-auth"
import { useEffect, useState } from "react"

import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

import { useRouter } from "next/navigation"
import { BoilerplateWithVotes } from "./sections/boilerplate-list"
import { useVoteModal } from "./vote-modal"

export default function VoteBtn({
  boilerplate,
  session,
  className,
  size = "lg",
}: {
  boilerplate: BoilerplateWithVotes
  session: Session | null
  className?: string
  size?: "sm" | "lg" | "md"
}) {
  const router = useRouter()
  const { open, close, modal: VoteModal } = useVoteModal(boilerplate.id)
  const { toast } = useToast()
  const [count, setCount] = useState(boilerplate._count.votes)
  const [active, setActive] = useState(!!boilerplate.votes.length)

  function getSize() {
    if (size === "sm") {
      return "h-[40px] w-[40px]"
    } else if (size === "md") {
      return "h-[60px] w-[60px]"
    } else {
      return "h-[80px] w-[80px]"
    }
  }

  function getSizeChevron() {
    if (size === "sm") {
      return "h-4 w-4"
    } else if (size === "md") {
      return "h-8 w-8"
    } else {
      return "h-10 w-10"
    }
  }

  useEffect(() => {
    setCount(boilerplate._count.votes)
  }, [boilerplate._count.votes])

  return (
    <>
      <VoteModal key={`vote-modal-${boilerplate.title}`} />
      <button
        key={`vote-btn-${boilerplate.id}-${boilerplate._count.votes}`}
        onClick={() => {
          if (!session) {
            toast({
              title: "Error",
              description: "You need to be logged in to vote...",
            })
            router.push("/signin")
            return
          }
          if (active) {
            setCount((prev) => prev - 1)
          } else {
            setCount((prev) => prev + 1)
          }
          setActive((prev) => !prev)
          vote(boilerplate.id)
            .catch(() => {
              toast({
                title: "Error",
                description: "Error while voting...",
              })
              setCount((prev) => (active ? prev + 1 : prev - 1))
              setActive((prev) => !prev)
            })
            .then(() => {
              if (!active) {
                open()
              }
            })
        }}
        type="button"
        role="button"
        className={cn(
          "group flex shrink-0 flex-col items-center justify-center rounded-md p-2 text-main-black shadow transition-all disabled:grayscale",
          getSize(),
          className,
          {
            "bg-neutral-100 text-primary": active,
            "bg-neutral-200 text-main-black hover:bg-neutral-100 hover:text-primary":
              !active,
          }
        )}
      >
        <ChevronUp className={cn("h-10 w-10 shrink-0 mx-auto", getSizeChevron())} />
        <span
          key={`vote-count-${boilerplate.id}-span`}
          className="font-cal text-lg font-semibold"
        >
          +{count ?? 0}
        </span>
      </button>
    </>
  )
}
